import { useCallback, useMemo } from 'react'
import { numberFormat } from 'shared/util'
import {
	Box, BoxProps, Alert, Stack,
	Typography, Chip,
	Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
	TableRowProps
} from "@mui/material"

import { TrialsPerson } from 'interface/trial'

type CellValue = {
	code?: string | null
	text: string | number | null
	style?: string
	unit?: string
}
type ColumnDef = {
	key: string,
	title: string,
	align: 'left' | 'center' | 'right' | 'chips',
	hidden?: boolean,
	val: (record: TrialsPerson) => CellValue | CellValue[] | null | undefined
}

const columns: ColumnDef[] = [
	{ key: 'name', title: '名前', align: 'left', val: record => ({ text: record.personName ?? null } as CellValue ) },
	{ key: 'roles', title: '役割', align: 'left', val: record => ({ text: record.roles?.join(', ') ?? null } as CellValue ) }
]

type TableHeaderRowProps = {
	columns: ColumnDef[]
} & TableRowProps
const TableHeaderRow = ({
	columns,
	...others
}: TableHeaderRowProps) => {
	return (
		<TableRow {...others}>
			{columns.filter(column => !column.hidden).map((column) => (
				<TableCell key={column.key} style={{ width: 0, whiteSpace: 'nowrap' }}>{column.title}</TableCell>
			))}
			<TableCell/>
		</TableRow>
	)  
}

const DisableFieldRenderer = () => (
	<TableCell aria-disabled style={{ background: 'GhostWhite' }}></TableCell>
)

type TextFieldRendererProps = {
	text: string
	unit: string | null | undefined
	align: ColumnDef['align']
}
const TextFieldRenderer = ({
	text,
	unit,
	align
}: TextFieldRendererProps) => text === '' ? <DisableFieldRenderer/> : (
	<TableCell
		sx={{
			whiteSpace: "nowrap",
			textAlign: align
		}}
	>
		{text}
		{unit ? (
			<Typography
				variant='body2'
				component='span'
				fontSize='80%'
				color='text.secondary'
			>
				{unit}
			</Typography>
		) : <></>}
	</TableCell>
)

type ArrayFieldRendererProps = {
	recordKey: string
	columnKey: string
	vals: CellValue[]
}
const ArrayFieldRenderer = ({
	recordKey,
	columnKey,
	vals
}: ArrayFieldRendererProps) => vals.length === 0 ? <DisableFieldRenderer/> : (
	<TableCell>
		<Stack direction="row" spacing={0.5}>
			{vals.map((v, j) => (
				<Chip
					key={`person_${recordKey}_${columnKey}_${j}`}
					size='small'
					label={(typeof v.text === 'number') ? numberFormat(v.text) : v.text}
				/>
			))}
		</Stack>
	</TableCell>
)

type PersonsTableCellProps = {
	column: ColumnDef
	record: TrialsPerson
}
const PersonsTableCell = ({
	column,
	record
}: PersonsTableCellProps) => {
	const value = useMemo(() => column.val(record), [column, record])
	return (value === null || value === undefined) ? (
		<DisableFieldRenderer/>
	) : (column.align === 'chips') ? (
		<ArrayFieldRenderer recordKey={record.kojinCode ?? record.personName} columnKey={column.key} vals={Array.isArray(value) ? value : [value]}/>
	) : (
		<TextFieldRenderer
			text={(Array.isArray(value) ? value : [value])
				.flatMap(v => v.text ? ((typeof v.text === 'number') ? numberFormat(v.text) : v.text) : [])
				.join(', ')}
			unit={Array.isArray(value) ? (value.length > 0 ? value[0].unit : null) : value.unit}
			align={column.align}
		/>
	)
}

type PersonsTableRowProps = {
	index: number
	columns: ColumnDef[]
	record: TrialsPerson
} & TableRowProps
const PersonsTableRow = ({
	columns,
	record,
	...others
}: PersonsTableRowProps) => (
	<TableRow {...others}>
		{columns.map(column => (
			<PersonsTableCell
				key={`${record.kojinCode}_${column.key}`}
				column={column}
				record={record}
			/>
		))}
	</TableRow>
)

type PersonsTableProps = {
	persons: TrialsPerson[]
	selectedPerson: string | null
	setSelectedPerson: (personKey: string | null) => void
} & BoxProps
export const PersonsTable = ({
	persons,
	selectedPerson,
	setSelectedPerson,
	...others
}: PersonsTableProps) => {
	const onClickedRow = useCallback(
		(person: TrialsPerson) => {
			const key = person.kojinCode ?? person.personName
			setSelectedPerson(selectedPerson === key ? null : key)
		},
		[setSelectedPerson, selectedPerson]
	)
	const validColumns = useMemo(
		() => columns.filter(column => !column.hidden),
		[]
	)
	return (
		<Box {...others} position='relative'>
			{persons.length === 0 ? (
				<Box padding={1}><Alert severity="info">対象が見つかりません.</Alert></Box>
			) : (
				<TableContainer sx={{ width: '100%', height: '100%' }}>
					<Table aria-label="doctors table" stickyHeader size="small" >
						<TableHead>
							<TableHeaderRow columns={validColumns}/>
						</TableHead>
						<TableBody>
							{persons.map((record, index) => (
								<PersonsTableRow
									key={`person-record-${index}`}
									index={index}
									columns={validColumns}
									record={record}
									selected={selectedPerson === (record.kojinCode ?? record.personName)}
									onClick={() => onClickedRow(record)}
									hover
									sx={{ cursor: 'pointer' }}
								/>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
	)
}